import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LazySection } from '../components/ui/LazySection';
import { X } from 'lucide-react';

export function History() {
  const { t } = useTranslation();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const paragraphs = Array.from({ length: 7 }, (_, i) => i + 1);
  
  const gallery = t('history_page.gallery', { returnObjects: true }) as Array<{
    image: string;
    title: string;
    description: string;
  }>;

  // Determine span classes for a Bento Grid effect
  // We have 7 items.
  // 0: span 2 cols
  // 1: span 1 col
  // 2: span 1 col
  // 3: span 1 col
  // 4: span 1 col
  // 5: span 2 cols
  // 6: span 1 col
  const getGridClasses = (index: number) => {
    switch(index) {
      case 0: return "md:col-span-2 lg:col-span-2 md:row-span-2"; // Large feature (2 cols, 2 rows)
      case 4: return "md:col-span-2 lg:col-span-2"; // Grupo de seminaristas (2 cols)
      case 5: return "md:col-span-2 lg:col-span-2"; // Wide feature (2 cols)
      default: return "col-span-1";
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 pb-20 text-zinc-300">
      
      {/* Hero Section */}
      <section className="relative w-full h-[40vh] md:h-[50vh] pt-24 flex items-center justify-center overflow-hidden border-b border-white/10">
        <div className="absolute inset-0">
          <img 
            src="/images/history/historia4.jpg" 
            alt="Seminario Historia" 
            className="w-full h-full object-cover object-top opacity-40 blur-[2px]"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/80 via-zinc-950/60 to-zinc-950" />
        </div>
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto mt-10">
          <h1 className="font-neocat text-3xl md:text-5xl lg:text-6xl font-bold tracking-widest mb-4 bg-gradient-to-r from-zinc-400 via-zinc-100 to-zinc-500 bg-clip-text text-transparent drop-shadow-lg">
            {t('history_page.title')}
          </h1>
          <div className="w-24 h-1 bg-gradient-to-r from-zinc-400 via-zinc-100 to-zinc-500 mx-auto rounded-full mb-6 opacity-80" />
        </div>
      </section>

      {/* Main Content */}
      <section className="px-4 sm:px-6 lg:px-8 mt-12 relative z-20">
        <div className="max-w-4xl mx-auto">
          
          <div className="font-neocat space-y-6 text-zinc-300 font-light leading-relaxed text-base md:text-lg text-justify">
            {paragraphs.map((p) => (
              <p key={p}>
                {t(`history_page.p${p}`)}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Bento Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 mt-12 border-t border-white/10">
        <h2 className="font-neocat text-2xl md:text-3xl font-bold tracking-widest text-center mb-16 bg-gradient-to-r from-zinc-400 via-zinc-100 to-zinc-500 bg-clip-text text-transparent">
          GALERÍA HISTÓRICA
        </h2>
        
        <LazySection>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-min">
            {gallery.map((item, index) => (
              <div 
                key={index} 
                className={`group relative flex flex-col bg-zinc-900/40 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden cursor-pointer hover:bg-zinc-900/80 transition-colors duration-500 shadow-xl hover:shadow-2xl hover:shadow-amber-900/20 ${getGridClasses(index)}`}
                onClick={() => setSelectedImage(`/images/history/${item.image}`)}
              >
                {/* Image Container */}
                <div className={`relative w-full overflow-hidden ${index === 0 ? 'h-64 md:h-full md:min-h-[300px]' : 'h-64'}`}>
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10 flex items-center justify-center">
                    <span className="text-white font-neocat tracking-widest border border-white/50 px-6 py-2 bg-black/50 backdrop-blur-sm rounded-sm">AMPLIAR</span>
                  </div>
                  <img 
                    src={`/images/history/${item.image}`} 
                    alt={item.title} 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                    loading="lazy"
                  />
                  {/* Subtle gradient to blend image into text area if stacked */}
                  <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-zinc-900/90 to-transparent pointer-events-none" />
                </div>

                {/* Text Content */}
                <div className={`flex flex-col justify-end p-6 md:p-8 z-20 ${index === 0 ? 'md:absolute md:inset-0 md:bg-gradient-to-t md:from-zinc-950 md:via-zinc-950/80 md:to-transparent' : ''}`}>
                  <h3 className={`font-neocat font-bold tracking-wider mb-3 bg-gradient-to-r from-zinc-400 via-zinc-100 to-zinc-500 bg-clip-text text-transparent ${index === 0 ? 'text-2xl md:text-3xl' : 'text-xl'}`}>
                    {item.title}
                  </h3>
                  <p className={`font-neocat text-zinc-300 font-light leading-relaxed text-justify ${index === 0 ? 'md:text-lg' : 'text-sm md:text-base'}`}>
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </LazySection>
      </section>

      {/* Image Modal Lightbox */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setSelectedImage(null)}
        >
          <button 
            className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors p-3 bg-white/10 hover:bg-white/20 rounded-full border border-white/20 z-50"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedImage(null);
            }}
          >
            <X size={28} />
          </button>
          <div className="relative max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center pointer-events-none">
            <img 
              src={selectedImage} 
              alt="Expanded History" 
              className="max-w-full max-h-full object-contain shadow-2xl drop-shadow-2xl pointer-events-auto cursor-default rounded-sm"
              onClick={(e) => e.stopPropagation()} 
            />
          </div>
        </div>
      )}

    </div>
  );
}
